import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CrudApiProvider } from '../../providers/crud-api/crud-api';
import { UserProvider } from '../../providers/user/user';
import { EnvVariables } from '../../environments/environment';
import { GeolocationProvider } from '../../providers/geolocation/geolocation';
import * as turf from '@turf/turf';
import * as debug from 'debug';
import * as _ from 'lodash';
import { ToolsProvider } from '../../providers/tools/tools';
import { ToastController, ActionSheetController } from 'ionic-angular';
import * as moment from 'moment';

@Component({
  selector: 'pw-reporting-buttons',
  templateUrl: 'pw-reporting-buttons.html',
})
export class PwReportingButtonsComponent {
  log = debug('app:PwReportingButtonsComponent');

  @Input() type = '';
  @Input() place: any = {};
  @Input() cardStyle = false;
  @Input() forceShowOptions = false;
  @Input() hideUpdateTitle = false;
  @Input() forceHideReporting = false; // for place cards
  @Input() updateOnly = false;
  @Output() parkingUpdate = new EventEmitter();
  @Output() weighStationUpdate = new EventEmitter();
  @Input() maxParkingStatus = 99;

  showParkingReporting: any = false;
  showWeighStationReporting: any = false;

  constructor(
    public crudApi: CrudApiProvider,
    public user: UserProvider,
    public env: EnvVariables,
    public geolocation: GeolocationProvider,
    public tools: ToolsProvider,
    public toastCtrl: ToastController,
    public actionSheetCtrl: ActionSheetController
  ) {}

  ngOnInit() {
    this.setOptions();
  }

  private setOptions() {
    let threshold = this.env.placeUpdateThreasholdYards;
    if (this.forceShowOptions) {
      // always show
      threshold = Infinity;
      this.log('always showing info');
      this.showParkingReporting = true;
      this.showWeighStationReporting = true;
      return;
    }
    if (this.forceHideReporting) {
      this.showParkingReporting = false;
      this.showWeighStationReporting = false;
      return;
    }
    this.geolocation.getRecentPosition().then(res => {
      this.log('geo recent location response', res);

      let userDistanceToMarker = turf.lineString([
        [res.longitude, res.latitude],
        _.get(this.place, 'location.coordinates'),
      ]);
      const type = _.get(this.place, 'placeType');

      // check for spamming
      let oldRecords = JSON.parse(localStorage.getItem('newParkingUpdate'));
      let placeId = this.place._id;
      let updateAllowed: boolean = false;
      let haveMatchedRecord: boolean;
      let currentTime = moment();
      let lastUpdated;

      _.each(oldRecords, record => {
        if (record.placeId === placeId) {
          haveMatchedRecord = true;
          lastUpdated = _.get(record, 'updateTimeStamp');
        } else {
          haveMatchedRecord = false;
        }
      });

      if (!oldRecords) {
        updateAllowed = true;
      } else {
        if (haveMatchedRecord) {
          let elapsedTime = moment.duration(currentTime.diff(lastUpdated));

          if (
            elapsedTime.years() === 0 &&
            elapsedTime.months() === 0 &&
            elapsedTime.days() === 0 &&
            elapsedTime.hours() > 1
          ) {
            updateAllowed = true;
            // remove match
            _.remove(oldRecords, record => {
              return record.placeId === placeId;
            });
          } else if (
            elapsedTime.years() !== 0 ||
            elapsedTime.months() !== 0 ||
            elapsedTime.days() !== 0
          ) {
            updateAllowed = true;
            // remove match
            _.remove(oldRecords, record => {
              return record.placeId === placeId;
            });
          }
        } else {
          updateAllowed = true;
        }
      }

      // when setting marker, check to ask for parking if close enough
      if (
        type === 'TRUCK_STOP' ||
        type === 'REST_AREA' ||
        type === 'WALMART' ||
        type === 'CASINO' ||
        type === 'LAUNDROMAT' ||
        type === 'WRECKER_SERVICES' ||
        type === 'INDEPENDENT_FUEL' ||
        type === 'SPEEDCO' ||
        type === 'PARKING'
      ) {
        this.log(
          'DISTANCE TO MARKER',
          turf.length(userDistanceToMarker, { units: 'yards' })
        );
        if (
          this.user.get('role') === 'admin' ||
          turf.length(userDistanceToMarker, { units: 'yards' }) <= threshold
        ) {
          if (updateAllowed) {
            this.showParkingReporting = true;
            this.showWeighStationReporting = false;
          }
        } else {
          this.log('hiding questions');
          this.showParkingReporting = false;
          this.showWeighStationReporting = false;
        }
      }

      if (type === 'WEIGH_STATION') {
        this.log('checking weigh station');
        if (
          this.user.get('role') === 'admin' ||
          turf.length(userDistanceToMarker, { units: 'yards' }) <= threshold
        ) {
          if (updateAllowed) {
            this.showParkingReporting = true;
            this.showWeighStationReporting = true;
          }
        } else {
          this.log('hiding questions');
          this.showParkingReporting = false;
          this.showWeighStationReporting = false;
        }
      }
    });
  }

  confirmParkingAtLocation(value) {
    // CREATING UPDATE RECORD IN LOCAL STORAGE
    let currentTime = moment();
    let thisPlaceId = this.place._id;
    let oldRecord = JSON.parse(localStorage.getItem('newParkingUpdate'));
    let newParkingUpdate = [];
    let updateObj = {
      placeId: thisPlaceId,
      updateTimeStamp: currentTime,
    };

    if (!oldRecord) {
      newParkingUpdate.push(updateObj);
      localStorage.setItem(
        'newParkingUpdate',
        JSON.stringify(newParkingUpdate)
      );
    } else {
      if (oldRecord.length < 4) {
        oldRecord.push(updateObj);
        localStorage.setItem('newParkingUpdate', JSON.stringify(oldRecord));
      } else {
        oldRecord.shift();
        oldRecord.push(updateObj);
        localStorage.setItem('newParkingUpdate', JSON.stringify(oldRecord));
      }
    }

    this.log('confirmParkingAtLocation function');
    let update = {
        availability: value,
      },
      marker = this.place;

    _.set(
      this.place,
      'info.parkingStatus',
      _.get(this.place, 'info.parkingStatus', [])
    );

    this.place.info.parkingStatus.unshift({
      timestamp: new Date().toISOString(),
      availability: value,
    });

    this.crudApi
      .put(`/api/places/${_.get(this.place, '_id')}/parkingStatus`, update)
      .then(
        res => {
          // if (!this.updateOnly) {
          this.showParkingReporting = false;
          this.showWeighStationReporting = false;
          this.parkingUpdate.emit();
          // }
          this.place.reportedParking = true;
          this.tools.logAnalyticsEvent('trackCustom', 'updated-parking', {
            place: _.get(this.place, '_id'),
          });
          const toast = this.toastCtrl.create({
            message: 'Thanks for your feedback',
            duration: 1500,
            cssClass: 'thankYouToast',
            position: 'bottom',
          });

          setTimeout(() => {
            this.tools.logEventAndFlush('tripParkingReported');
            if (value === 'OPEN') {
              this.tools.logEventAndFlush('tripParkingOpen');
            } else if (value === 'NONE') {
              this.tools.logEventAndFlush('tripParkingFull');
            }
          }, 1500);

          toast.present();
        },
        e => {
          this.log('an error occured', e);
        }
      );
  }

  confirmWeighStationOpen(status) {
    this.log('confirmWeighStationOpen function');
    let update = {
        status: status,
      },
      marker = this.place;

    _.set(
      this.place,
      'info.openForBusiness',
      _.get(this.place, 'info.openForBusiness', [])
    );

    this.place.info.openForBusiness.unshift({
      timestamp: new Date().toISOString(),
      status: status,
    });

    this.crudApi
      .put(
        `/api/places/${_.get(this.place, '_id')}/weightStationStatus`,
        update
      )
      .then(
        res => {
          // if (!this.updateOnly) {
          this.showParkingReporting = false;
          this.showWeighStationReporting = false;
          this.weighStationUpdate.emit();
          // }
          this.place.reportedWeighStation = true;
          const toast = this.toastCtrl.create({
            message: 'Thanks for your feedback',
            duration: 1500,
            cssClass: 'thankYouToast',
            position: 'bottom',
          });
          toast.present();

          setTimeout(() => {
            this.tools.logEventAndFlush('tripWeighStationReported');
            if (status === 'OPEN') {
              this.tools.logEventAndFlush('tripWeighStationOpen');
            } else if (status === 'CLOSED') {
              this.tools.logEventAndFlush('tripWeighStationClosed');
            }
          }, 1500);
        },
        e => {
          this.log('an error occured', e);
        }
      );
  }
  openParkingStatusPopup() {
    const actionSheet = this.actionSheetCtrl.create({
      buttons: [
        {
          text: 'Lots of Parking',
          cssClass: 'ActionSheetPW--open',
          handler: () => {
            this.confirmParkingAtLocation('OPEN');
          },
        },
        {
          text: 'Some Parking',
          cssClass: 'ActionSheetPW--some',
          handler: () => {
            this.confirmParkingAtLocation('SOME');
          },
        },
        {
          text: 'No Parking',
          cssClass: 'ActionSheetPW--no',
          handler: () => {
            this.confirmParkingAtLocation('NONE');
          },
        },
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            this.log('Cancel clicked');
          },
        },
      ],
    });
    actionSheet.present();
  }
}



// WEBPACK FOOTER //
// ./src/components/pw-reporting-buttons/pw-reporting-buttons.ts